<template>
  <Card
    title="Assortiment"
    subtitle="Opties die je bij de import hebt uitgezet kun je hier weer aanzetten, en andersom."
  >
    <p v-if="error" class="catalog-assortment__error">{{ error }}</p>
    <p v-if="loaded && !options.length" class="catalog-assortment__empty">
      Dit product heeft geen opties om aan of uit te zetten.
    </p>

    <div v-for="option in options" :key="option.id" class="catalog-assortment__option">
      <label class="catalog-assortment__option-head">
        <input
          type="checkbox"
          :checked="option.enabled"
          :disabled="option.locked"
          @change="setOption(option, $event.target.checked)"
        />
        <span>{{ option.label || option.id }}</span>
        <span v-if="option.locked" class="catalog-assortment__lock">{{ lockLabel(option) }}</span>
        <span v-if="option.choices.length > bulkThreshold" class="catalog-assortment__count">
          {{ enabledCount(option.choices) }} van {{ option.choices.length }} aan
        </span>
      </label>

      <div v-if="option.enabled && option.choices.length" class="catalog-assortment__choices">
        <template v-if="option.choices.length <= bulkThreshold">
          <label
            v-for="choice in option.choices"
            :key="choice.id"
            class="catalog-assortment__choice"
          >
            <input v-model="choice.enabled" type="checkbox" />
            {{ choice.label || choice.id }}
          </label>
        </template>
        <template v-else>
          <div class="catalog-assortment__toolbar">
            <input
              v-model="uiFor(option).query"
              type="search"
              class="catalog-assortment__search"
              placeholder="Zoek een keuze"
            />
            <Btn size="sm" variant="secondary" @click="setChoices(visibleChoices(option), true)">
              {{ uiFor(option).query ? 'Gevonden aan' : 'Alles aan' }}
            </Btn>
            <Btn size="sm" variant="secondary" @click="setChoices(visibleChoices(option), false)">
              {{ uiFor(option).query ? 'Gevonden uit' : 'Alles uit' }}
            </Btn>
          </div>

          <template v-if="uiFor(option).query || !groupsFor(option).length">
            <label
              v-for="choice in visibleChoices(option)"
              :key="choice.id"
              class="catalog-assortment__choice"
            >
              <input v-model="choice.enabled" type="checkbox" />
              {{ choice.label || choice.id }}
            </label>
          </template>
          <template v-else>
            <div
              v-for="group in groupsFor(option)"
              :key="group.name"
              class="catalog-assortment__group"
              :class="{ 'catalog-assortment__group--open': isGroupOpen(option, group.name) }"
            >
              <div class="catalog-assortment__group-head">
                <input
                  type="checkbox"
                  :checked="enabledCount(group.choices) === group.choices.length"
                  :indeterminate="
                    enabledCount(group.choices) > 0 &&
                    enabledCount(group.choices) < group.choices.length
                  "
                  @change="setChoices(group.choices, $event.target.checked)"
                />
                <button
                  type="button"
                  class="catalog-assortment__group-toggle"
                  @click="toggleGroup(option, group.name)"
                >
                  <span>{{ group.name }}</span>
                  <span class="catalog-assortment__count">
                    {{ enabledCount(group.choices) }}/{{ group.choices.length }}
                  </span>
                </button>
              </div>
              <div v-if="isGroupOpen(option, group.name)" class="catalog-assortment__group-choices">
                <label
                  v-for="choice in group.choices"
                  :key="choice.id"
                  class="catalog-assortment__choice"
                >
                  <input v-model="choice.enabled" type="checkbox" />
                  {{ choice.label || choice.id }}
                </label>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>

    <div v-if="options.length" class="catalog-assortment__footer">
      <Btn variant="primary" :loading="saving" :disabled="!loaded" @click="save">
        Assortiment opslaan
      </Btn>
    </div>
  </Card>
</template>

<script>
  import { computed, onMounted, reactive, ref } from 'vue';
  import Card from '@/components/partials/Card.vue';
  import Btn from '@/components/partials/Btn.vue';
  import { catalogService } from '@/services/catalogService';
  import { useToastStore } from '@/store/toastStore';

  const LOCK_LABELS = {
    axis: 'Bepaalt de prijs',
    formula_input: 'Zit in de berekening',
    constraint_target: 'Nodig voor een geldige maat',
  };

  const OTHER_GROUP = 'Overig';

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const groupName = (choice) => {
    if (choice.group) return choice.group;
    const label = String(choice.label || '').trim();
    const cut = label.search(/\s+-\s*|\s*\(/);
    return cut > 0 ? label.slice(0, cut).trim() : label;
  };

  const buildGroups = (choices) => {
    const byName = new Map();
    for (const choice of choices) {
      const name = groupName(choice);
      if (!byName.has(name)) byName.set(name, []);
      byName.get(name).push(choice);
    }
    const groups = [];
    const rest = [];
    for (const [name, members] of byName) {
      if (members.length > 1) groups.push({ name, choices: members });
      else rest.push(...members);
    }
    if (rest.length > choices.length / 2) return [];
    if (rest.length) groups.push({ name: OTHER_GROUP, choices: rest });
    return groups.length > 1 ? groups : [];
  };

  export default {
    name: 'CatalogAssortmentCard',
    components: { Card, Btn },
    props: {
      installationId: { type: String, required: true },
    },
    emits: ['saved'],
    setup(props, { emit }) {
      const toastStore = useToastStore();
      const options = ref([]);
      const loaded = ref(false);
      const saving = ref(false);
      const error = ref('');
      const choiceUi = reactive({});
      const bulkThreshold = 12;

      const uiFor = (option) => {
        if (!choiceUi[option.id]) choiceUi[option.id] = { query: '', openGroups: [] };
        return choiceUi[option.id];
      };

      const isGroupOpen = (option, name) => uiFor(option).openGroups.includes(name);

      const toggleGroup = (option, name) => {
        const ui = uiFor(option);
        ui.openGroups = isGroupOpen(option, name)
          ? ui.openGroups.filter((item) => item !== name)
          : [...ui.openGroups, name];
      };

      const groupsByOption = computed(() => {
        const result = {};
        for (const option of options.value) {
          if (option.choices.length > bulkThreshold) {
            result[option.id] = buildGroups(option.choices);
          }
        }
        return result;
      });

      const groupsFor = (option) => groupsByOption.value[option.id] || [];

      const visibleChoices = (option) => {
        const query = normalize(uiFor(option).query).trim();
        if (!query) return option.choices;
        return option.choices.filter((choice) =>
          normalize(choice.label || choice.id).includes(query)
        );
      };

      const enabledCount = (choices) => choices.filter((choice) => choice.enabled).length;

      const setChoices = (choices, enabled) => {
        choices.forEach((choice) => {
          choice.enabled = enabled;
        });
      };

      const setOption = (option, enabled) => {
        if (option.locked) return;
        option.enabled = enabled;
      };

      const lockLabel = (option) => LOCK_LABELS[option.lock_reason] || 'Vast';

      const load = async () => {
        error.value = '';
        try {
          const result = await catalogService.getInstallation(props.installationId);
          options.value = (result.dealer?.options || []).map((option) => ({
            ...option,
            choices: (option.choices || []).map((choice) => ({ ...choice })),
          }));
          loaded.value = true;
        } catch (err) {
          error.value = err?.response?.data?.error || 'Assortiment laden mislukt';
        }
      };

      const save = async () => {
        saving.value = true;
        error.value = '';
        const disabledOptionIds = options.value
          .filter((option) => !option.locked && !option.enabled)
          .map((option) => option.id);
        const disabledChoiceIds = options.value
          .filter((option) => option.enabled)
          .flatMap((option) => option.choices.filter((choice) => !choice.enabled))
          .map((choice) => choice.id);
        try {
          await catalogService.updateInstallation(props.installationId, {
            disabled_option_ids: disabledOptionIds,
            disabled_choice_ids: disabledChoiceIds,
          });
          toastStore.addToast({ type: 'success', message: 'Assortiment opgeslagen' });
          emit('saved');
        } catch (err) {
          const details = err?.response?.data?.details;
          error.value =
            (Array.isArray(details) && details[0]?.message) ||
            err?.response?.data?.error ||
            'Opslaan mislukt';
        } finally {
          saving.value = false;
        }
      };

      onMounted(load);

      return {
        options,
        loaded,
        saving,
        error,
        bulkThreshold,
        uiFor,
        groupsFor,
        isGroupOpen,
        toggleGroup,
        visibleChoices,
        enabledCount,
        setChoices,
        setOption,
        lockLabel,
        save,
      };
    },
  };
</script>

<style scoped lang="scss">
  .catalog-assortment {
    &__option {
      padding: 0.75rem 0;
      border-bottom: 1px solid var(--slate-100);
    }

    &__option-head,
    &__choice {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__choices {
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
      margin: 0.5rem 0 0 1.5rem;
    }

    &__lock,
    &__count {
      color: var(--slate-500);
      @include fontSize('sm');
    }

    &__count {
      margin-left: auto;
    }

    &__toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem;
    }

    &__group {
      border: 1px solid var(--slate-100);
      border-radius: 0.5rem;
    }

    &__group-head {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.4rem 0.6rem;
    }

    &__group-toggle {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      border: 0;
      background: none;
      padding: 0;
      cursor: pointer;
      text-align: left;
    }

    &__group-choices {
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
      padding: 0 0.6rem 0.6rem 2rem;
    }

    &__search {
      flex: 1;
      min-width: 12rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      padding: 0.4rem 0.6rem;
    }

    &__footer {
      margin-top: 1rem;
    }

    &__empty {
      margin: 0;
      color: var(--slate-500);
    }

    &__error {
      margin: 0 0 0.75rem;
      color: var(--red-700);
    }
  }
</style>
